import * as ToggleMenuFlyoutEvent from '@ori-events/toggle-menu-flyout';
import * as UserAction from '@ori-events/user-action';
import { Notification, NotificationFilled } from '@ori-ui/icons';
import { Badge, IconButton, styled } from '@ori-ui/mui';
import { useToken } from '@ori/auth';
import { CLIENT_APPLICATION } from '@ori/fetching';
import { useGetCustomerUnseenNotificationsCount } from '@ori/global-api-client-react';
import type { FC } from 'react';
import { useCallback, useState } from 'react';

import { CLICK_NOTIFICATION_ICON } from '../constants';
import { useHover } from '../hooks';
import { dispatchToggleMenu } from '../utils';

const NotificationBellIconRoot = styled('div')(({ theme }) => ({
  marginLeft: theme.spacing(4),

  [theme.breakpoints.up('lg')]: {
    marginLeft: theme.spacing(6),
  },

  [theme.breakpoints.up('xl')]: {
    marginLeft: theme.spacing(8),
  },
}));

const NotificationBellIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.grey.A700,
  fontSize: theme.typography.h4.fontSize,
  padding: 0,

  '&:hover, &:focus, &:active': {
    background: 'none',
    color: theme.palette.grey.A700,
  },
}));

export const NotificationBellIcon: FC = () => {
  const [ref, Icon] = useHover(NotificationFilled, Notification);
  const [isClicked, setIsClicked] = useState(false);
  const { customerId } = useToken();
  const { data } = useGetCustomerUnseenNotificationsCount(customerId, {
    [CLIENT_APPLICATION]: 'Web' /* notifications API requires value `Web` */,
  });
  const { count } = data ?? { count: 0 };

  const handleClick = useCallback(() => {
    UserAction.dispatch(CLICK_NOTIFICATION_ICON);
    setIsClicked(true);

    dispatchToggleMenu(ToggleMenuFlyoutEvent.MenuTypes.Notifications);
  }, []);

  return (
    <NotificationBellIconRoot>
      <NotificationBellIconButton
        ref={ref}
        onClick={handleClick}
      >
        <Badge
          color="error"
          badgeContent={count}
          max={99}
          invisible={isClicked || count === 0}
        >
          <Icon fontSize="inherit" />
        </Badge>
      </NotificationBellIconButton>
    </NotificationBellIconRoot>
  );
};
